import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import ChecklistTile from '../../../../incase-domain/components/checkbox/ChecklistTile';
import Label from '../../../../incase-domain/components/label/Label';
import {IElementTextInput} from '../../../../incase-domain/elements/textInput';
import {
    FORM_FIELD_ELEMENT_NAME_MAX,
    TEXT_INPUT_LABEL_MAX,
    TEXT_INPUT_PLACEHOLDER_MAX,
} from '../../../../incase-domain/validators/elements/constant';
import {fieldElementSchema} from '../../../../incase-domain/validators/elements/form';
import {inputElementValidator} from '../../../../incase-domain/validators/elements/inputElement';
import {getErrorClasses} from '../../../../utils/validation';
import SectionTitle from '../../../elements/common/SectionTitle';
import {TABS} from '../../form/part/tabs';
import DebouncedTextInput from '../common/debounced/DebouncedTextInput';
import DebouncedNumberInput from '../common/debounced/DebounceNumberInput';
import ElementShadow from '../common/elementShadow';
import FontSelect from '../common/fontSelect';
import FormElementOptions from '../common/formOptions/formElementOptions';
import TextInputValidation from './TextInputValidation';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedTextInputElementComponentProps {
    errors: IValidationErrorObject | null;
    overrideHighlightedElement?: IElementTextInput;
    overrideHighlightedElementChangeHandler?: {single: (field: keyof IElementTextInput, value: unknown) => void};
    inForm?: boolean;
    activeTab: string;
}

HighlightedTextInputElementComponent.defaultProps = {
    inForm: false,
};

function HighlightedTextInputElementComponent(props: HighlightedTextInputElementComponentProps): ReactElement {
    const {errors, overrideHighlightedElement, overrideHighlightedElementChangeHandler, inForm, activeTab} = props;
    const highlightedElement = useHighlightedElement() as IElementTextInput;
    const element = overrideHighlightedElement || highlightedElement;
    const updateHighlightedElement = useSetHighlightedElement();
    const changeHandler = overrideHighlightedElementChangeHandler || updateHighlightedElement;
    const {t} = useTranslation();

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={element} highlightedElementChangeHandler={changeHandler} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <AnimationForm
                    appearAfter={element.appearAfter || 0}
                    elementAnimations={element.elementAnimations}
                    highlightedElementChangeHandler={changeHandler}
                />
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.preferences')} />
                    <ChecklistTile
                        value={!!element.blockingQuestion}
                        label={t('designSidepanel.blockingInput')}
                        help={t('designSidepanel.blockingQuestionExpl')}
                        onChange={(value) => changeHandler.single('blockingQuestion', value)}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.content')} />
                <div className={`l-group__item ${getErrorClasses(errors, 'label')}`}>
                    <Label>{t('designSidepanel.label')}</Label>
                    <DebouncedTextInput
                        className="c-sidepanel__input"
                        value={element.label}
                        onChange={(value) => changeHandler.single('label', value)}
                        validator={(val) => {
                            const validation = inputElementValidator.shape.label.safeParse(val);
                            return validation.success;
                        }}
                        maxLength={TEXT_INPUT_LABEL_MAX}
                    />
                </div>

                <div className={`${getErrorClasses(errors, 'placeholder')}`}>
                    <Label>{t('designSidepanel.placeholder')}</Label>
                    <DebouncedTextInput
                        className="c-sidepanel__input"
                        value={element.placeholder}
                        onChange={(value) => changeHandler.single('placeholder', value)}
                        validator={(val) => {
                            const validation = inputElementValidator.shape.placeholder.safeParse(val);
                            return validation.success;
                        }}
                        maxLength={TEXT_INPUT_PLACEHOLDER_MAX}
                    />
                </div>
            </div>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.textInputValidation')} />

                <TextInputValidation highlightedElement={element} highlightedElementChangeHandler={changeHandler} errors={errors} />
            </div>

            {inForm && (
                <>
                    <div className="l-group--form">
                        <div className="l-group__item">
                            <Label>{t('editorPage.form.fieldName')}</Label>
                            <DebouncedTextInput
                                className="c-sidepanel__input"
                                value={element.htmlName || ''}
                                onChange={(value) => changeHandler.single('htmlName', value)}
                                validator={(val) => {
                                    const validation = fieldElementSchema.shape.htmlName.safeParse(val);
                                    return validation.success;
                                }}
                                maxLength={FORM_FIELD_ELEMENT_NAME_MAX}
                            />
                        </div>
                    </div>

                    <FormElementOptions highlightedElement={element} highlightedElementChangeHandler={changeHandler} />
                </>
            )}

            <>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.style')} />
                    <div className="u-flex u-flex-col u-gap-3">
                        <FontSelect highlightedElement={element} highlightedElementChangeHandler={changeHandler} />
                        <div className="u-flex u-v-center u-h-between u-gap-2">
                            <Label>{t('designSidepanel.fontSize')}</Label>
                            <DebouncedNumberInput
                                value={element.font.size || 1}
                                onChange={(newValue: number) =>
                                    changeHandler.single('font', {
                                        ...element.font,
                                        size: newValue,
                                    })
                                }
                                step={0.1}
                                min={0.1}
                            />
                        </div>
                    </div>
                </div>
            </>
        </>
    );
}

export default HighlightedTextInputElementComponent;
